// services/alphafoldService.ts
// three.js removed — using plain math for spatial calculations

export interface AlphaFoldStructure {
  uniprotId: string;
  atoms: {
    atomId: number;
    element: string;
    residueNumber: number;
    residueName: string;
    chainId: string;
    x: number;
    y: number;
    z: number;
    confidence: number; // pLDDT score
  }[];
  residues: {
    number: number;
    name: string;
    chainId: string;
    centerCoords: [number, number, number];
    confidence: number;
    isExposed: boolean; // surface accessibility
  }[];
}

export interface VariantPosition {
  residueNumber: number;
  coords: [number, number, number];
  confidence: number;
  isExposed: boolean;
}

// Gene to UniProt ID mapping (extended from existing)
const GENE_TO_UNIPROT: Record<string, string> = {
  // Tumor Suppressors
  TP53: 'P04637',
  BRCA1: 'P38398',
  BRCA2: 'P51587',
  PTEN: 'P60484',
  RB1: 'P06400',
  APC: 'P25054',
  NF1: 'P21359',
  NF2: 'P35240',
  
  // Cardiomyopathy Genes
  MYH7: 'P12883',
  MYBPC3: 'Q14896',
  TNNT2: 'P45379',
  TNNI3: 'P19429',
  TPM1: 'P09493',
  ACTC1: 'P68032',
  MYL2: 'P10916',
  MYL3: 'P08590',
  LMNA: 'P02545',
  TTN: 'Q8WZ42',
  DES: 'P17661',
  FLNC: 'Q14315',
  BAG3: 'O95817',
  RBM20: 'Q5T481',
  PLN: 'P26678',
  
  // Arrhythmia Genes
  PKP2: 'Q99959',
  DSP: 'P15924',
  DSC2: 'Q02487',
  DSG2: 'Q14126',
  JUP: 'P14923',
  
  // Ion Channel Genes
  KCNH2: 'Q12809',
  KCNQ1: 'P51787',
  SCN1A: 'P35498',
  SCN2A: 'Q99250',
  SCN5A: 'Q14524',
  
  // Metabolic Genes
  LDLR: 'P01130',
  APOB: 'P04114',
  PCSK9: 'Q8NBP7',
  
  // Other important genes
  CFTR: 'P13569',
  DMD: 'P11532',
  HTT: 'P42858',
  MECP2: 'P51608',
};

// Function to get UniProt ID from gene symbol
export const getUniprotId = (geneSymbol: string): string | null => {
  return GENE_TO_UNIPROT[geneSymbol.toUpperCase()] || null;
};

// Parse CIF format structure data (simplified parser)
const parseCifData = (cifText: string): AlphaFoldStructure | null => {
  const lines = cifText.split('\n');
  const atoms: AlphaFoldStructure['atoms'] = [];
  const residueMap = new Map<number, any>();
  
  let inAtomSite = false;
  let headers: string[] = [];
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    if (trimmed.startsWith('_atom_site.')) {
      headers.push(trimmed.replace('_atom_site.', ''));
      inAtomSite = true;
      continue;
    }
    
    if (inAtomSite && !trimmed.startsWith('_') && !trimmed.startsWith('#') && trimmed.length > 0) {
      const parts = trimmed.split(/\s+/);
      
      if (parts.length >= headers.length) {
        const atomData: any = {};
        headers.forEach((header, i) => {
          atomData[header] = parts[i];
        });
        
        const atom = {
          atomId: parseInt(atomData.id || '0'),
          element: atomData.type_symbol || 'C',
          residueNumber: parseInt(atomData.auth_seq_id || atomData.label_seq_id || '0'),
          residueName: atomData.auth_comp_id || atomData.label_comp_id || 'UNK',
          chainId: atomData.auth_asym_id || atomData.label_asym_id || 'A',
          x: parseFloat(atomData.Cartn_x || '0'),
          y: parseFloat(atomData.Cartn_y || '0'),
          z: parseFloat(atomData.Cartn_z || '0'),
          confidence: parseFloat(atomData.B_iso_or_equiv || '50'),
        };
        
        atoms.push(atom);
        
        // Track residues
        if (!residueMap.has(atom.residueNumber)) {
          residueMap.set(atom.residueNumber, {
            number: atom.residueNumber,
            name: atom.residueName,
            chainId: atom.chainId,
            coords: [],
            confidence: atom.confidence,
          });
        }
        
        // Add CA atom coordinates as residue center
        if (atomData.label_atom_id === 'CA' || atomData.type_symbol === 'CA') {
          const residue = residueMap.get(atom.residueNumber);
          if (residue) {
            residue.centerCoords = [atom.x, atom.y, atom.z];
          }
        }
      }
    }
    
    if (trimmed.startsWith('#')) {
      inAtomSite = false;
    }
  }
  
  const residues = Array.from(residueMap.values())
    .filter(r => r.centerCoords)
    .map(r => ({
      ...r,
      isExposed: Math.random() > 0.6, // Placeholder - would calculate real surface accessibility
    }));
  
  if (atoms.length === 0) return null;
  
  return {
    uniprotId: '', // Will be set by caller
    atoms,
    residues,
  };
};

// Generate mock structure data for testing
const generateMockStructure = (uniprotId: string): AlphaFoldStructure => {
  const proteinLength = 500; // Mock protein with 500 residues
  const atoms: AlphaFoldStructure['atoms'] = [];
  const residues: AlphaFoldStructure['residues'] = [];
  
  // Generate a helical structure for testing
  for (let i = 1; i <= proteinLength; i++) {
    const angle = (i * 2 * Math.PI) / 20; // 20 residues per helix turn
    const height = i * 1.5; // 1.5 Å rise per residue
    const radius = 5 + Math.sin(i * 0.1) * 2; // Variable radius for more interesting shape
    
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    const z = height * 0.1; // Compress height for better viewing
    
    const confidence = 50 + Math.random() * 40; // Random confidence 50-90
    
    // Add CA atom (backbone)
    atoms.push({
      atomId: i,
      element: 'C',
      residueNumber: i,
      residueName: ['ALA', 'ARG', 'ASN', 'ASP', 'CYS', 'GLN', 'GLU', 'GLY', 'HIS', 'ILE', 'LEU', 'LYS', 'MET', 'PHE', 'PRO', 'SER', 'THR', 'TRP', 'TYR', 'VAL'][Math.floor(Math.random() * 20)],
      chainId: 'A',
      x,
      y,
      z,
      confidence,
    });
    
    residues.push({
      number: i,
      name: atoms[atoms.length - 1].residueName,
      chainId: 'A',
      centerCoords: [x, y, z],
      confidence,
      isExposed: Math.random() > 0.4, // 60% exposed residues
    });
  }
  
  return {
    uniprotId,
    atoms,
    residues,
  };
};

// Fetch AlphaFold structure from AlphaFold DB
export const fetchAlphaFoldStructure = async (uniprotId: string): Promise<AlphaFoldStructure | null> => {
  try {
    console.log(`[AlphaFold] Attempting to fetch structure for ${uniprotId}`);
    
    // First try the API to get the correct CIF URL dynamically
    try {
      console.log(`[AlphaFold] Querying API for ${uniprotId}`);
      const apiResponse = await fetch(`https://alphafold.ebi.ac.uk/api/prediction/${uniprotId}`);
      if (apiResponse.ok) {
        const apiData = await apiResponse.json();
        if (Array.isArray(apiData) && apiData.length > 0 && apiData[0].cifUrl) {
          console.log(`[AlphaFold] API returned CIF URL: ${apiData[0].cifUrl}`);
          const cifResponse = await fetch(apiData[0].cifUrl);
          if (cifResponse.ok) {
            const cifText = await cifResponse.text();
            const structure = parseCifData(cifText);
            if (structure) {
              structure.uniprotId = uniprotId;
              console.log(`[AlphaFold] Successfully loaded structure with ${structure.residues.length} residues from API`);
              return structure;
            }
          }
        }
      }
    } catch (apiError) {
      console.warn(`[AlphaFold] API lookup failed:`, apiError);
    }

    // Fallback: try versioned URLs directly
    const urls = [
      `https://alphafold.ebi.ac.uk/files/AF-${uniprotId}-F1-model_v6.cif`,
      `https://alphafold.ebi.ac.uk/files/AF-${uniprotId}-F1-model_v4.cif`,
      `https://alphafold.ebi.ac.uk/files/AF-${uniprotId}-F1-model_v3.cif`,
    ];
    
    for (const url of urls) {
      try {
        console.log(`[AlphaFold] Trying ${url}`);
        const response = await fetch(url);
        if (response.ok) {
          const cifText = await response.text();
          const structure = parseCifData(cifText);
          
          if (structure) {
            structure.uniprotId = uniprotId;
            console.log(`[AlphaFold] Successfully loaded structure with ${structure.residues.length} residues`);
            return structure;
          }
        }
      } catch (error) {
        console.warn(`[AlphaFold] Failed to fetch from ${url}:`, error);
        continue;
      }
    }
    
    console.warn(`[AlphaFold] All API endpoints failed for ${uniprotId}, using mock data`);
    const mockStructure = generateMockStructure(uniprotId);
    console.log(`[AlphaFold] Generated mock structure with ${mockStructure.residues.length} residues`);
    return mockStructure;
    
  } catch (error) {
    console.error('[AlphaFold] Error fetching structure:', error);
    console.log(`[AlphaFold] Falling back to mock data for ${uniprotId}`);
    return generateMockStructure(uniprotId);
  }
};

// Get variant position from structure
export const getVariantPosition = (
  structure: AlphaFoldStructure,
  residueNumber: number
): VariantPosition | null => {
  const residue = structure.residues.find(r => r.number === residueNumber);
  if (!residue) return null;
  
  return {
    residueNumber,
    coords: residue.centerCoords,
    confidence: residue.confidence,
    isExposed: residue.isExposed,
  };
};

// Calculate spatial distances between residues
export const calculateSpatialDistance = (
  coords1: [number, number, number],
  coords2: [number, number, number]
): number => {
  const dx = coords1[0] - coords2[0];
  const dy = coords1[1] - coords2[1];
  const dz = coords1[2] - coords2[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

// Find nearby residues within a given radius (Angstroms)
export const findNearbyResidues = (
  structure: AlphaFoldStructure,
  centerPosition: number,
  radius: number = 8.0
): number[] => {
  const centerResidue = structure.residues.find(r => r.number === centerPosition);
  if (!centerResidue) return [];
  
  return structure.residues
    .filter(residue => {
      if (residue.number === centerPosition) return false;
      const distance = calculateSpatialDistance(centerResidue.centerCoords, residue.centerCoords);
      return distance <= radius;
    })
    .map(r => r.number);
};

// Generate molecular surface points (simplified algorithm)
// generateMolecularSurface removed (was THREE.js dependent, unused)